import * as React from 'react';
import {createViewConfig} from '@sail/engine';
import {tokens} from '@sail/tokens';
import {useUpdateEffect} from '@sail/react';
import {AnimatePresence, motion, useAnimate} from '@sail/motion';
import {view} from '../../view';
import type {CodePuncherProps} from './types';
import {useInputSelection} from './hooks';
import type {CodePuncherDigitData} from './unstable_CodePuncherDigit';
import {unstable_CodePuncherDigit as UnstableCodePuncherDigit} from './unstable_CodePuncherDigit';

import {CodePuncherInput} from './CodePuncherInput';
import type {FormFieldProps} from '../FormField';
import {
  ErrorText,
  FormField,
  SuccessText,
  useFormFieldProps,
} from '../FormField';
import {useFieldContexts} from '../hooks/useFieldContexts';
import {Spinner} from '../Spinner';

type unstable_CodePuncherProps = CodePuncherProps & {
  /**
   * Success message to display below the input. Also applies the success visual state to the digits.
   */
  success?: React.ReactNode | void;

  /**
   * Loading message to display below the input. Also applies the loading visual state to the digits.
   */
  loading?: React.ReactNode | void;
};

const DIGIT_GAP = tokens['space.small'];

export const unstable_NativeCodePuncherConfig = createViewConfig({
  props: {} as unstable_CodePuncherProps,
  name: 'unstable_NativeCodePuncher',
  defaults: {
    value: '',
    disabled: false,
  },
});

const CLEAR_ATTRIBUTES_AFTER_SUBMIT = ['data-com-onepassword-filled'] as const;

const NativeCodePuncher = unstable_NativeCodePuncherConfig.createView(
  ({
    render,
    onClick,
    onChange,
    onSelectionChange,
    disabled,
    onComplete,
    value = '',
    maxLength = 6,
    autoFocus = false,
    noAutofill = false,
    error,
    success,
    loading,
    subviews,
    ...props
  }) => {
    if (
      process.env.NODE_ENV !== 'production' &&
      [loading, error, success].filter(Boolean).length > 1
    ) {
      throw new Error(
        'CodePuncher: `loading`, `error`, and `success` cannot be set at the same time.',
      );
    }

    const [shakeScope, shake] = useAnimate<HTMLDivElement>();

    const [internalValue, setInternalValue] = React.useState(value);
    const {id, ariaLabel, ariaLabelledBy, ariaDescribedBy} =
      useFieldContexts(props);

    const inputRef = React.useRef<HTMLInputElement>(null);

    const selection = useInputSelection(inputRef);

    React.useEffect(() => {
      onSelectionChange?.(selection);
    }, [selection, onSelectionChange]);

    // Sync external value changes
    useUpdateEffect(() => {
      if (value !== internalValue) {
        if (
          value === '' &&
          internalValue.length >= maxLength &&
          inputRef.current
        ) {
          for (const attribute of CLEAR_ATTRIBUTES_AFTER_SUBMIT) {
            inputRef.current.removeAttribute(attribute);
          }

          inputRef.current.setSelectionRange(0, 0);
        }

        setInternalValue(value);

        if (
          inputRef.current?.ownerDocument.activeElement === inputRef.current
        ) {
          // Dispatch event to recalculate selection due to external change
          inputRef.current?.dispatchEvent(new Event('selectionchange'));
        }
      }
    }, [value]);

    React.useLayoutEffect(() => {
      if (internalValue.length !== maxLength) {
        return;
      }

      const animationFrameId = requestAnimationFrame(() => {
        onComplete?.(internalValue);
      });

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }, [internalValue, maxLength, onComplete]);

    // Shake the digit group when an error is newly set.
    const prevErrorRef = React.useRef(!!error);
    React.useEffect(() => {
      const errorJustSet = !!error && !prevErrorRef.current;

      if (errorJustSet && shakeScope.current) {
        shake(
          shakeScope.current,
          {x: [0, -6, 6, -4, 4, -2, 2, 0]},
          {duration: 0.4, ease: 'easeInOut'},
        );
      }

      prevErrorRef.current = !!error;
    }, [error, shake, shakeScope]);

    // Wave each digit up and back down when entering success state.
    const prevSuccessForWaveRef = React.useRef(!!success);
    React.useEffect(() => {
      const successJustSet = !!success && !prevSuccessForWaveRef.current;
      if (successJustSet && shakeScope.current) {
        Array.from(shakeScope.current.children).forEach((el, index) => {
          shake(
            el as HTMLElement,
            {scale: [1, 1.07, 1]},
            {delay: index * 0.04, duration: 0.1, ease: 'easeOut'},
          );
        });
      }
      prevSuccessForWaveRef.current = !!success;
    }, [success, shake, shakeScope]);

    // Handle input changes - update internal state and call external callback
    const handleOnChange = React.useCallback(
      (newValue: string) => {
        setInternalValue(newValue);
        onChange?.(newValue);
      },
      [onChange],
    );

    const handleOnClick = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (disabled) {
          return;
        }

        // eslint-disable-next-line @sail/ui-no-reflow
        inputRef.current?.focus();

        onClick?.(event);
      },
      [onClick, disabled],
    );

    const digits = React.useMemo(() => {
      const isRangeSelection = !!selection && selection.start !== selection.end;
      const isLoading = !!loading;

      return Array.from({length: maxLength}).reduce<
        Array<CodePuncherDigitData>
      >((acc, _, idx) => {
        const digitValue = internalValue[idx] || '';

        const isActive =
          !disabled &&
          !!selection &&
          selection.end >= idx &&
          selection.start <= idx;

        return [
          ...acc,
          {
            disabled,
            active: isActive && !isLoading,
            value: digitValue,
            error,
            success: !!success,
            loading: isLoading,
            isRangeSelection,
          },
        ];
      }, []);
    }, [
      maxLength,
      internalValue,
      selection,
      disabled,
      error,
      success,
      loading,
    ]);

    const content = React.useMemo(() => {
      if (render) {
        return render(digits);
      }

      return (
        <view.div ref={shakeScope} inherits={subviews.digitGroup}>
          {digits.map((slot, index) => (
            <UnstableCodePuncherDigit
              key={index} // eslint-disable-line react/no-array-index-key
              inherits={subviews.digit}
              {...slot}
              style={{
                transitionDelay:
                  slot.success || slot.error || slot.loading
                    ? `${index * 40}ms`
                    : undefined,
              }}
            />
          ))}
        </view.div>
      );
    }, [digits, render, subviews.digitGroup, subviews.digit, shakeScope]);

    return (
      <view.div {...props} onClick={handleOnClick}>
        {content}

        <view.div
          css={{
            inset: 'space.0',
            position: 'absolute',
          }}
        >
          <CodePuncherInput
            id={id}
            ref={inputRef}
            disabled={disabled}
            value={internalValue}
            maxLength={maxLength}
            autoFocus={autoFocus}
            noAutofill={noAutofill}
            onChange={handleOnChange}
            isComplete={internalValue.length >= maxLength}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
          />
        </view.div>
      </view.div>
    );
  },
  {
    css: {
      stack: 'x',
      gap: 'small',
      width: 'fit',
      position: 'relative',
      alignItems: 'center',
    },
    subviews: {
      digitGroup: {
        css: {
          stack: 'x',
          gap: DIGIT_GAP,
          alignItems: 'center',
          // Create a stacking context to contain CodePuncherDigit z-index
          // values. Without this, the active digit (zIndex: 1) would overlap
          // the hidden input, preventing iOS from showing the paste menu on tap.
          position: 'relative',
          zIndex: 0,
        },
      },
    },
  },
);

export const unstable_CodePuncherConfig = createViewConfig({
  name: 'unstable_CodePuncher',
  props: {} as FormFieldProps<
    unstable_CodePuncherProps,
    typeof NativeCodePuncher
  >,
});

type CodePuncherMessageProps = {
  loading?: React.ReactNode | void;
  error?: React.ReactNode | void;
  success?: React.ReactNode | void;
};

const messageMotion = motion('slideDown');

// AnimatePresence must wrap the branch elements directly (not a stable
// wrapper) so it can see the key change across renders and animate the
// exit/enter as a pair instead of swapping instantly.
const CodePuncherMessage = ({
  loading,
  error,
  success,
}: CodePuncherMessageProps) => {
  let content: React.ReactNode = null;

  if (loading) {
    content = (
      <view.div
        key="loading"
        role="status"
        css={{
          stack: 'x',
          gap: 'xsmall',
          alignItems: 'center',
          color: 'subdued',
          font: 'label.small',
        }}
        uses={[messageMotion]}
      >
        <Spinner size="small" role="presentation" />
        <view.span>{loading}</view.span>
      </view.div>
    );
  } else if (error) {
    // Suppress duplicate `alert`; FormField's hidden ErrorText handles aria-describedby.
    content = (
      <ErrorText key="error" role="presentation" uses={[messageMotion]}>
        {error}
      </ErrorText>
    );
  } else if (success) {
    content = (
      <SuccessText key="success" uses={[messageMotion]}>
        {success}
      </SuccessText>
    );
  }

  return <AnimatePresence mode="wait">{content}</AnimatePresence>;
};

/**
 * unstable_CodePuncher is an experimental variant of CodePuncher with enhanced
 * animations and visual feedback. Enable it via the `enableRefreshedCodePuncher` flag
 * in UiProvider.
 */
export const unstable_CodePuncher = unstable_CodePuncherConfig.createView(
  (props) => {
    const [fieldProps, controlProps] = useFormFieldProps(props);

    return (
      <FormField
        {...fieldProps}
        hiddenElements={[...(fieldProps.hiddenElements ?? []), 'error']}
      >
        <view.div css={{stack: 'y', gap: 'xsmall'}}>
          <NativeCodePuncher
            {...controlProps}
            subviews={props.subviews}
            error={!!fieldProps.error}
            inherits={props.subviews.input}
          />
          <CodePuncherMessage
            loading={props.loading}
            error={fieldProps.error}
            success={props.success}
          />
        </view.div>
      </FormField>
    );
  },
);
